import React, { useState } from 'react';

type LanguageSection = 'interface' | 'concepts';

/**
 * Appreciative Language page — glossary of terms used across AppreciativeAI.
 * A toggle at the top lets visitors switch between the User Interface and
 * Concepts glossaries.
 */
export function AppreciativeLanguagePage(): React.JSX.Element {
  const [activeSection, setActiveSection] = useState<LanguageSection>('interface');

  return (
    <div className="flex flex-col items-center px-4 py-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        {/* Prism SVG */}
        <div className="mb-8 flex justify-center">
          <svg
            width="260"
            height="120"
            viewBox="0 0 260 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="opacity-90"
          >
            <line x1="0" y1="60" x2="85" y2="60" stroke="#1D1C1D" strokeWidth="3" strokeLinecap="round" />
            <polygon points="90,20 150,20 120,100" fill="none" stroke="#1D1C1D" strokeWidth="2" />
            <line x1="160" y1="28" x2="260" y2="8" stroke="#C75B5B" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="161" y1="35" x2="260" y2="25" stroke="#D4874D" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="162" y1="42" x2="260" y2="42" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="162" y1="49" x2="260" y2="58" stroke="#5B9E6F" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="161" y1="56" x2="260" y2="75" stroke="#4D9E9E" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="160" y1="63" x2="260" y2="92" stroke="#4A7FB5" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="158" y1="70" x2="260" y2="108" stroke="#7B5EA7" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </div>

        <h1 className="mb-2 text-center text-3xl font-bold text-text-primary">
          Appreciative Language
        </h1>
        <p className="mb-8 text-center text-sm font-medium text-text-muted italic">
          Use language that grows what fuels you at work.
        </p>

        {/* Section toggle */}
        <div
          className="mb-10 flex justify-center"
          role="tablist"
          aria-label="Glossary sections"
        >
          <div className="inline-flex rounded-lg border border-border bg-surface-muted p-1">
            <button
              type="button"
              role="tab"
              aria-selected={activeSection === 'interface'}
              onClick={() => setActiveSection('interface')}
              className={
                activeSection === 'interface'
                  ? 'rounded-md bg-white px-5 py-2 text-sm font-semibold text-text-primary shadow-sm'
                  : 'rounded-md px-5 py-2 text-sm font-medium text-text-muted hover:text-text-primary'
              }
            >
              User Interface
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeSection === 'concepts'}
              onClick={() => setActiveSection('concepts')}
              className={
                activeSection === 'concepts'
                  ? 'rounded-md bg-white px-5 py-2 text-sm font-semibold text-text-primary shadow-sm'
                  : 'rounded-md px-5 py-2 text-sm font-medium text-text-muted hover:text-text-primary'
              }
            >
              Concepts
            </button>
          </div>
        </div>

        {/* User Interface Section */}
        {activeSection === 'interface' && (
        <section className="mb-12">
          <h2 className="mb-6 text-xl font-semibold text-text-primary border-b border-border pb-2">
            User Interface
          </h2>
          <dl className="space-y-6">
            <GlossaryItem
              term="Action"
              definition="Actions are action items that an AAI agent identifies as being possible right now that you want to take note of. In an AAI Agent conversation, hover over a response to save it as an Action ✅ for future reference on your personal Dashboard in the Actions & Insights section (available to: Collaborator, Practitioner)."
            />
            <GlossaryItem
              term="Agent"
              definition="AppreciativeAI agents, built on Anthropic's Claude platform, are autonomous AI applications designed to offer insight, not answers. Each agent is trained in a strength‑based, action‑oriented approach to coaching and collaboration that keeps you as the decision maker. Their role is to help you make your work better by doing what you can, from where you are, with what you have. They may be capable of making decisions, but they are trained to leave the decision‑making — and the action‑taking — to you."
            />
            <GlossaryItem
              term="Coaching Perspective"
              definition="Every AAI agent's initial conversation post asks you if you would like to provide your coaching perspective (Analysis, Exploration, Protection, or Relationship Building) to the AAI agent so it can better tailor its responses for you. There is no obligation to do this and you can also provide your coaching perspective in any way you choose. The intention is to offer you a quick way to help the AAI agent understand where you're coming from so it can more easily provide you with accurate insight. AAI coaching perspectives are based on categories from the 16 MBTI Personalities."
            />
            <GlossaryItem
              term="Analysis"
              definition="Analysis is one of AAI's 4 coaching perspectives. Choose Analysis when you're working on something existing — something you want to examine, organize, redesign, optimize, or strategize."
            />
            <GlossaryItem
              term="Collaborator"
              definition="Collaborator access gives you free, full access to AppreciativeAI during a defined testing period. As a Collaborator, your anonymized data and any feedback you choose to provide help shape future development of AppreciativeAI."
            />
            <GlossaryItem
              term="Early Tester"
              definition="See Collaborator."
            />
            <GlossaryItem
              term="Exploration"
              definition="Exploration is one of AAI's 4 coaching perspectives. Choose Exploration if you're looking to start something new, to create, experiment, discover, innovate, or engage others in something new."
            />
            <GlossaryItem
              term="Insight"
              definition="Insights are key takeaways from an AAI agent response that you want to explore further. Hover over a response to save it as an Insight 💡 for future reference in your Dashboard (available to: Collaborator, Practitioner)."
            />
            <GlossaryItem
              term="Journey"
              definition="Journeys mark what you are working with AAI agents to achieve — the issue you want to resolve or the opportunity you want to grow. You can group any number of agent conversations into a Journey for easy reference in your Dashboard. Create a Journey by naming it and stating your Desired Outcome as if it has already been achieved. You can then map conversations to a Journey from your Dashboard or when starting a new session."
            />
            <GlossaryItem
              term="Practitioner"
              definition="Practitioner access gives you full access to AppreciativeAI as a paid membership until you cancel."
            />
            <GlossaryItem
              term="Protection"
              definition="Protection is one of AAI's 4 coaching perspectives. Choose Protection if you want to put or secure a foundation under something, structuring, supporting, preserving, verifying, stabilizing to ensure what you're working on is unshakable."
            />
            <GlossaryItem
              term="Relationship Building"
              definition="Relationship Building is one of AAI's 4 coaching perspectives. Choose Relationship Building when you want to focus on communication, motivation, and connection, to express something, motivate yourself, or guide, connect, or unite people."
            />
            <GlossaryItem
              term="Session"
              definition="Sessions are conversations with AAI agents. Starter access enables unsaved sessions. Collaborator and Practitioner access enables saved sessions you can revisit anytime."
            />
            <GlossaryItem
              term="Starter"
              definition="Starter access gives you free, unsaved conversations with all 7 AAI agents so you can begin exploring AppreciativeAI without commitment."
            />
          </dl>
        </section>
        )}

        {/* Concepts Section */}
        {activeSection === 'concepts' && (
        <section>
          <h2 className="mb-6 text-xl font-semibold text-text-primary border-b border-border pb-2">
            Concepts
          </h2>
          <dl className="space-y-6">
            <GlossaryItem
              term="AI Drift"
              definition="When AI's answers make perfect logical sense but drift away from your context, your intention, or your next steps — leaving you with guidance that's ineffective, inefficient, or unactionable."
            />
            <GlossaryItem
              term="Appreciative Approach"
              definition="AppreciativeAI is built on an approach to problem solving, innovation, and change that assumes you already have what you need to begin — leaving only the work of growing (appreciating) what matters most to you (what you appreciate). AAI agents use an appreciative approach that keeps you in the driver's seat, offering perspectives and insights that help you do what you can, from where you are, with what you have, to make your work better right now."
            />
            <GlossaryItem
              term="Dialogic Approach"
              definition="AppreciativeAI uses a dialogic approach to developing ideas and taking action — an approach grounded in conversation. Dialogic development recognizes that perspective shapes perception, and that new perspectives emerge through dialogue. Change develops step‑by‑step through conversational interactions, narratives, and stories. This allows individuals to create for themselves and collectively through conversation."
            />
            <GlossaryItem
              term="Evolutionary Purpose"
              definition="Purpose understood as something that continually emerges from learning and real‑time experience. It evolves as the organization listens to customer, market, and employee signals and responds to what is becoming possible."
            />
            <GlossaryItem
              term="Flip"
              definition="A flip reframes a perspective from &ldquo;weak&rdquo; to &ldquo;strong.&rdquo; You acknowledge the issue, then flip it to identify the strengths you have — or need — to address it."
            />
            <GlossaryItem
              term="Generative Leadership"
              definition="Leadership that creates environments where people feel energized, capable, and supported. It focuses on building systems that increase capacity, creativity, and well‑being."
            />
            <GlossaryItem
              term="Generative Listening"
              definition="The fourth level of listening in Otto Scharmer's Theory U. It is listening with an open will — in ways that allow new insights, possibilities, and futures to emerge. The four levels of Theory U listening: 1. Downloading — Hearing what you already expect. 2. Factual Listening — Hearing with an open mind. 3. Empathic Listening — Hearing with an open heart. 4. Generative Listening — Hearing with an open will, allowing something new to arise."
            />
            <GlossaryItem
              term="Lens"
              definition="A lens is a perspective. Just as colored lenses change what you see, different work lenses help you see your situation in new ways. Looking through all AppreciativeAI lenses gives you a complete perspective on your work situation."
            />
            <GlossaryItem
              term="One Organization"
              definition="Seeing the organization as a single, interconnected whole. It is the shift from trying to influence isolated parts to aligning with the organization's overall direction, so individual and team efforts contribute to coherent, system‑wide outcomes."
            />
            <GlossaryItem
              term="Self‑Management"
              definition="A way of working where people hold clear responsibilities and the authority to act on them. Instead of relying on top‑down control, the organization creates coherence through shared agreements, transparency, and trust, enabling individuals and teams to manage themselves effectively."
            />
            <GlossaryItem
              term="Systems Thinking"
              definition="Understanding how people, processes, and structures interact to create outcomes beyond what any individual part can produce alone. It highlights patterns, relationships, and leverage points that allow individuals and teams to positively influence the larger system."
            />
          </dl>
        </section>
        )}
      </div>
    </div>
  );
}

function GlossaryItem({ term, definition }: { term: string; definition: string }) {
  return (
    <div>
      <dt className="text-sm font-semibold text-text-primary">{term}</dt>
      <dd className="mt-1 text-sm leading-relaxed text-text-secondary">{definition}</dd>
    </div>
  );
}
